import React, { useState } from 'react';
import { Task, useUpdateTask, PleriTaskStatus } from '../../api/tasks';
import { useListConversations } from '../../api/conversations';
import { useRunTask } from '../../api/task-runs';
import { getNextScheduledTime } from './utils';
import DateWithTooltip from '../../components/common/DateWithTooltip';
import { Feedback, Tooltip } from '@plerionhq/design-system';
import { PlayCircleIcon } from '@heroicons/react/24/outline';
import { TaskStatusBadge } from '../../components/common/TaskStatusBadge';
import { ExtendedMarkdown } from '../../components/Pleri/components/MessageContent/ExtendedMarkdown';
import { ExternalLink } from '../../components/common/ExternalLink';
import './TaskOverview.css';

import { useTranslation } from 'react-i18next';
import { scheduleText } from './schedule-text';

interface TaskOverviewProps {
  task: Task;
  userProfile: { email?: string } | undefined;
}

export const TaskOverview: React.FC<TaskOverviewProps> = ({
  task,
  userProfile,
}) => {
  const { t } = useTranslation('pleri/pages');
  const [feedback, setFeedback] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  const {
    mutate: runTask,
    isPending: isRunning,
    variables: runningTaskVars,
    reset,
  } = useRunTask();

  const { mutate: updateTask, isPending: isUpdating } = useUpdateTask(
    task.scope,
  );

  // Get all executions for stats and last execution
  const { data: executionsData } = useListConversations({
    source: 'task',
    sourceId: task.id,
    limit: 100, // Get more for stats
  });

  const lastExecution = executionsData?.conversations?.[0];
  const allExecutions = executionsData?.conversations || [];

  const nextScheduledTime = getNextScheduledTime(task.schedule);

  const handleRun = () => {
    runTask(
      { scope: task.scope, taskId: task.id },
      {
        onSuccess: () => {
          setFeedback({
            type: 'success',
            message: t('taskStartedSuccessfully'),
          });
        },
        onError: () => {
          setFeedback({
            type: 'error',
            message: t('failedToStartTaskPleaseTryAgain'),
          });
        },
        onSettled: () => {
          reset();
        },
      },
    );
  };

  const handleToggleStatus = (checked: boolean) => {
    const newStatus = checked ? PleriTaskStatus.Active : PleriTaskStatus.Paused;

    updateTask(
      {
        taskId: task.id,
        request: { status: newStatus },
      },
      {
        onSuccess: () => {
          setFeedback({
            type: 'success',
            message: checked ? t('taskActivated') : t('taskPaused'),
          });
        },
        onError: () => {
          setFeedback({
            type: 'error',
            message: checked
              ? t('failedToActivateTaskPleaseTryAgain')
              : t('failedToPauseTaskPleaseTryAgain'),
          });
        },
      },
    );
  };

  const isTaskEnabled = task.status === PleriTaskStatus.Active;
  const isTaskDisabled = task.status !== PleriTaskStatus.Active;
  const isCurrentlyRunning = isRunning && runningTaskVars?.taskId === task.id;
  const isRunButtonDisabled = isCurrentlyRunning || isTaskDisabled;

  const getRunButtonTooltip = (): string | null => {
    if (!isRunButtonDisabled) {
      return null;
    }
    if (isCurrentlyRunning) {
      return t('taskIsCurrentlyRunning');
    }
    if (isTaskDisabled) {
      return t('taskIsPausedActivateTheTaskToRunIt');
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Quick Actions */}
      <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
        <div>
          <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {t('quickActions')}
          </h3>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            {t('runThisTaskManually')}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Toggle Status Switch - Displays enabled/disabled status and toggle button */}
          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {isTaskEnabled ? t('active') : t('paused')}
          </span>
          <button
            type="button"
            tabIndex={0}
            aria-label={isTaskEnabled ? t('pauseTask') : t('activateTask')}
            aria-checked={isTaskEnabled}
            aria-disabled={isUpdating}
            role="switch"
            onClick={() => handleToggleStatus(!isTaskEnabled)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleToggleStatus(!isTaskEnabled);
              }
            }}
            disabled={isUpdating}
            className={[
              'relative inline-flex h-4 w-7 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:ring-2 focus:ring-orange-600 focus:ring-offset-2 focus:outline-none',
              isTaskEnabled ? 'bg-orange-600' : 'bg-gray-200 dark:bg-gray-700',
              isUpdating ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
            ].join(' ')}
          >
            <span className="sr-only">
              {isTaskEnabled ? t('pause') : t('activate')}
            </span>
            <span
              className={[
                isTaskEnabled ? 'translate-x-3' : 'translate-x-0',
                'pointer-events-none relative inline-block h-3 w-3 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out dark:bg-gray-900',
              ].join(' ')}
            />
          </button>

          {getRunButtonTooltip() ? (
            <Tooltip label={getRunButtonTooltip()}>
              <button
                onClick={handleRun}
                disabled={isRunButtonDisabled}
                data-testid="run-task-button"
                className="flex items-center gap-2 rounded-lg bg-orange-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-orange-700 disabled:opacity-50"
              >
                <PlayCircleIcon className="h-4 w-4" />
                {isCurrentlyRunning ? t('running') : t('runTask')}
              </button>
            </Tooltip>
          ) : (
            <button
              onClick={handleRun}
              disabled={isRunButtonDisabled}
              data-testid="run-task-button"
              className="flex items-center gap-2 rounded-lg bg-orange-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-orange-700 disabled:opacity-50"
            >
              <PlayCircleIcon className="h-4 w-4" />
              {isCurrentlyRunning ? t('running') : t('runTask')}
            </button>
          )}
        </div>
      </div>
      {feedback && (
        <Feedback variant={feedback.type}>{feedback.message}</Feedback>
      )}
      {/* Status Card */}
      <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
        <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3 lg:grid-cols-4">
          {/* Status */}
          <div>
            <dt className="text-xs font-medium text-gray-500 dark:text-gray-400">
              {t('common:status')}
            </dt>
            <dd className="mt-1 w-fit">
              <TaskStatusBadge status={task.status} />
            </dd>
          </div>

          {/* Last Executed */}
          <div>
            <dt className="text-xs font-medium text-gray-500 dark:text-gray-400">
              {t('lastExecuted')}
            </dt>
            <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">
              {lastExecution ? (
                <DateWithTooltip date={lastExecution.createdAt} />
              ) : (
                <span className="text-gray-400 dark:text-gray-500">
                  {t('never')}
                </span>
              )}
            </dd>
          </div>

          {/* Total Executions */}
          <div>
            <dt className="text-xs font-medium text-gray-500 dark:text-gray-400">
              {t('totalExecutions')}
            </dt>
            <dd className="mt-1 text-sm font-semibold text-gray-900 dark:text-gray-100">
              {allExecutions.length}
            </dd>
          </div>

          {/* Created By */}
          <div>
            <dt className="text-xs font-medium text-gray-500 dark:text-gray-400">
              {t('createdBy')}
            </dt>
            <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">
              {task.scope === 'system' ? (
                <span className="inline-block rounded-md bg-orange-100 px-2 py-1 text-xs font-medium text-orange-700 dark:bg-gray-700">
                  {t('common:plerion')}
                </span>
              ) : userProfile && userProfile.email ? (
                <span className="inline-block rounded-md bg-blue-100 px-2 py-1 text-xs font-medium text-blue-700 dark:bg-blue-900">
                  {userProfile.email}
                </span>
              ) : (
                '—'
              )}
            </dd>
          </div>

          {/* Created Date */}
          <div>
            <dt className="text-xs font-medium text-gray-500 dark:text-gray-400">
              {t('createdDate')}
            </dt>
            <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">
              <DateWithTooltip date={task.createdAt} />
            </dd>
          </div>

          {/* Schedule */}
          <div>
            <dt className="text-xs font-medium text-gray-500 dark:text-gray-400">
              {t('schedule')}
            </dt>
            <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">
              {task.schedule ? (
                scheduleText(t, task.schedule)
              ) : (
                <span className="text-gray-400 dark:text-gray-500">
                  {t('manual')}
                </span>
              )}
            </dd>
          </div>

          {/* Next Run */}
          <div>
            <dt className="text-xs font-medium text-gray-500 dark:text-gray-400">
              {t('nextRun')}
            </dt>
            <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">
              {isTaskDisabled ? (
                <Tooltip label={t('taskIsPausedActivateTheTaskTo')}>
                  <span className="cursor-help text-gray-400 dark:text-gray-500">
                    N/A
                  </span>
                </Tooltip>
              ) : nextScheduledTime ? (
                <DateWithTooltip date={nextScheduledTime} />
              ) : (
                <span className="text-gray-400 dark:text-gray-500">
                  {t('notScheduled')}
                </span>
              )}
            </dd>
          </div>
        </div>
      </div>
      {/* Description */}
      {(task.description ||
        (task.configurations?.excludeRepoDueToInactivity &&
          task.configurations.excludeRepoDueToInactivity.length > 0)) && (
        <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
          <h2 className="mb-3 text-sm font-medium text-gray-900 dark:text-gray-100">
            {t('common:description')}
          </h2>
          <div id="task-description">
            {task.description && (
              <ExtendedMarkdown content={task.description} />
            )}
            {task.configurations?.excludeRepoDueToInactivity &&
              task.configurations.excludeRepoDueToInactivity.length > 0 && (
                <div className="mt-4 border-t border-gray-200 pt-4 dark:border-gray-700">
                  <p className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('excludedAsNoPullRequestsWereMerged')}
                  </p>
                  <ul className="list-inside list-disc space-y-1 pl-4 text-sm text-gray-600 dark:text-gray-400">
                    {task.configurations.excludeRepoDueToInactivity.map(
                      (repo) => (
                        <li key={repo.id}>
                          <ExternalLink href={repo.link}>
                            {repo.id}
                          </ExternalLink>
                        </li>
                      ),
                    )}
                  </ul>
                </div>
              )}
          </div>
        </div>
      )}
    </div>
  );
};
